import { Injectable } from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Messaging,
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
  type Unsubscribe,
} from 'firebase/messaging';
import { environment } from '../../../environments/environment';

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
}

/**
 * Low-level Firebase Cloud Messaging helpers for the customer web app.
 * Safe to call repeatedly — initializes the Firebase app at most once.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseMessagingService {
  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private foregroundUnsubscribe: Unsubscribe | null = null;

  isBrowserSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator
    );
  }

  isSecureContext(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.isSecureContext || window.location.hostname === 'localhost';
  }

  async ensureInitialized(): Promise<Messaging | null> {
    if (!this.isBrowserSupported() || !this.isSecureContext()) {
      console.warn(
        '[FirebaseMessaging] Browser does not support web push or is not a secure context.',
      );
      return null;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) {
      console.warn(
        '[FirebaseMessaging] Firebase Messaging is not supported in this browser.',
      );
      return null;
    }

    const config = this.getFirebaseConfig();
    if (!config) {
      console.warn('[FirebaseMessaging] Firebase web configuration is missing.');
      return null;
    }

    if (!this.app) {
      this.app =
        getApps().length > 0
          ? getApp()
          : initializeApp({
              apiKey: config.apiKey,
              authDomain: config.authDomain,
              projectId: config.projectId,
              storageBucket: config.storageBucket,
              messagingSenderId: config.messagingSenderId,
              appId: config.appId,
            });
    }

    if (!this.messaging) {
      this.messaging = getMessaging(this.app);
    }

    return this.messaging;
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isBrowserSupported()) {
      return 'denied';
    }
    if (Notification.permission === 'granted') {
      return 'granted';
    }
    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      return await Notification.requestPermission();
    } catch {
      return 'denied';
    }
  }

  async getRegistrationToken(): Promise<string | null> {
    const messaging = await this.ensureInitialized();
    if (!messaging) {
      return null;
    }

    const config = this.getFirebaseConfig();
    if (!config?.vapidKey) {
      console.warn('[FirebaseMessaging] VAPID key is missing.');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { scope: '/' },
      );
      await navigator.serviceWorker.ready;

      const token = await getToken(messaging, {
        vapidKey: config.vapidKey,
        serviceWorkerRegistration: registration,
      });

      return token || null;
    } catch (error) {
      console.warn('[FirebaseMessaging] Failed to obtain FCM token.', error);
      return null;
    }
  }

  /**
   * Subscribes to foreground FCM messages. Replaces any previous listener.
   */
  async listenForForegroundMessages(
    handler: (payload: MessagePayload) => void,
  ): Promise<void> {
    const messaging = await this.ensureInitialized();
    if (!messaging) {
      return;
    }

    this.stopForegroundListener();
    this.foregroundUnsubscribe = onMessage(messaging, handler);
  }

  stopForegroundListener(): void {
    if (this.foregroundUnsubscribe) {
      this.foregroundUnsubscribe();
      this.foregroundUnsubscribe = null;
    }
  }

  async deleteCurrentToken(): Promise<void> {
    try {
      const messaging = await this.ensureInitialized();
      if (!messaging) {
        return;
      }
      await deleteToken(messaging);
    } catch {
      // Best-effort cleanup; logout must continue.
    }
  }

  private getFirebaseConfig(): FirebaseWebConfig | null {
    const firebase = (
      environment as { firebase?: Partial<FirebaseWebConfig> }
    ).firebase;

    if (
      !firebase?.apiKey ||
      !firebase.authDomain ||
      !firebase.projectId ||
      !firebase.messagingSenderId ||
      !firebase.appId ||
      !firebase.vapidKey
    ) {
      return null;
    }

    return {
      apiKey: firebase.apiKey,
      authDomain: firebase.authDomain,
      projectId: firebase.projectId,
      storageBucket: firebase.storageBucket ?? '',
      messagingSenderId: firebase.messagingSenderId,
      appId: firebase.appId,
      vapidKey: firebase.vapidKey,
    };
  }
}
